import { BingMapCoordinates, FullLocation } from 'ts/types';
import { BingMapApi } from 'containers/BingMap';
import { GRANTED } from 'constants/permissions';
import { useLocation } from 'containers/Location/useLocation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateTemporalLocation } from 'redux/actions/locationActions';
import {
  selectBrowserLocationCoordinates,
  selectGeolocationStatusSelector,
} from 'redux/selectors/browserLocation';
import type { GoogleMapCoordinates } from 'ts/types';

const BING_MAPS_KEY = process.env.REACT_APP_BING_MAPS_KEY || '';

export const isBingMapCoordinate = (
  coordinate: BingMapCoordinates | GoogleMapCoordinates,
): coordinate is BingMapCoordinates => {
  // @ts-ignore
  return coordinate?.latitude !== undefined;
};

export const parseCoordinatesToBing = (
  coordinates: BingMapCoordinates | GoogleMapCoordinates,
): BingMapCoordinates => {
  if (isBingMapCoordinate(coordinates)) {
    return coordinates;
  } else {
    return {
      latitude: coordinates?.lat,
      longitude: coordinates?.lng,
    };
  }
};

const useLocationWithBing = () => {
  const dispatch = useDispatch();
  const { autocomplete, ...location } = useLocation();
  const bingMapApi = useRef<BingMapApi | undefined>(undefined);

  const browserCoordinates = useSelector(selectBrowserLocationCoordinates);
  const geolocationStatus = useSelector(selectGeolocationStatusSelector);

  const [bingMapInitializedLocation, setBingMapInitializedLocation] =
    useState(false);

  const handleCoordinatesSelected = useCallback(
    (coordinates: BingMapCoordinates) => {
      if (coordinates.latitude && coordinates.longitude) {
        bingMapApi.current
          ?.reverseGeocode(coordinates)
          .then((currentLocation: FullLocation) => {
            dispatch(updateTemporalLocation(currentLocation));
            location.setZoom(bingMapApi.current?.map.getZoom());
            location.setPins([currentLocation]);
            location.setIsDefaultLocation(false);
          })
          .catch(e => {
            console.error('Error reverse geocoding', e);
          });
      }
    },
    [dispatch, location],
  );

  const handleGeolocation = useCallback(() => {
    location.handleGeolocation();
    if (geolocationStatus === GRANTED) {
      handleCoordinatesSelected(browserCoordinates as BingMapCoordinates);
    }
    setBingMapInitializedLocation(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    browserCoordinates,
    handleCoordinatesSelected,
    location.handleGeolocation,
    geolocationStatus,
  ]);

  const handleOptionSelected = (locationSelected: FullLocation | undefined) => {
    autocomplete.handleOptionSelected();
    dispatch(updateTemporalLocation(locationSelected));
    location.setCenter(locationSelected?.coordinates);
  };

  const setBingMapApiRef = (bingMapApiRef: BingMapApi) => {
    bingMapApi.current = bingMapApiRef;
  };

  useEffect(() => {
    if (!bingMapInitializedLocation && !autocomplete.inputValue) {
      handleGeolocation();
    }
  }, [autocomplete.inputValue, bingMapInitializedLocation, handleGeolocation]);

  // Wait 300 milliseconds before running the search
  useEffect(() => {
    if (
      location.debouncedSearchValue &&
      location.debouncedSearchValue.length > 3
    ) {
      const timer = setTimeout(() => {
        autocomplete.setLoading(true);
        bingMapApi.current
          ?.geocode(location.debouncedSearchValue)
          .then((locations: FullLocation[]) => {
            autocomplete.setOptions(locations);
            autocomplete.setLoading(false);
          })
          .catch(e => console.log('error while getting options', e))
          .finally(() => {
            autocomplete.setLoading(false);
          });
      }, 300);
      return () => {
        clearTimeout(timer);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.debouncedSearchValue]);

  const clearField = () => {};
  const getAutocompleteOptionLabel = () => '';

  return {
    apiKey: BING_MAPS_KEY,
    autocomplete,
    handleCoordinatesSelected,
    handleGeolocation,
    handleOptionSelected,
    location,
    setBingMapApiRef,
    clearField,
    getAutocompleteOptionLabel,
  };
};

export default useLocationWithBing;
